import { FC, JSX, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'src/utils/axios';
import { messageForHouseholdMemberError } from 'src/utils/errors/contactErrorMessages';
import {
  CertificationProperty,
  CertHouseholdData,
  type AddUserHouseholdMemberPayload,
} from 'src/types/Certification';
import { fadeStyle } from 'src/components/NewRenterInterface/constants';
import {
  useCertificationMembersV2,
  type UnlinkedHHM,
} from 'src/hooks/useCertificationMembersV2';
import { type MemberType } from '../HouseholdComposition/HoHTransferDialog';
import { isMinorMemberType } from '../memberRoleColors';
import { findMemberContactConflict } from './householdContactUtils';

interface AddHouseholdMemberScreenProps {
  household: CertHouseholdData;
  property?: CertificationProperty;
  /** Required for the new flow: identifies the certification we're linking to. */
  certificationId?: number;
  onSubmit: () => void;
  onCancel: () => void;
  sx?: SxProps;
}

interface AlertMessage {
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
}

type AddPhase = 'select' | 'form';

const getUhmLabel = (m: UnlinkedHHM): string => {
  const middle = m.middle_name && m.middle_name.trim() ? m.middle_name : '';
  return [m.first_name, middle, m.last_name]
    .filter((p) => p && p.trim())
    .join(' ')
    .trim();
};

const getInitials = (first?: string | null, last?: string | null): string =>
  [first, last]
    .filter((n): n is string => Boolean(n))
    .map((n) => n[0].toUpperCase())
    .join('');

// Maps a member_type_name to the UserHousehold role accepted by the
// my-household members endpoint. The endpoint rejects 'hoh', and Head of
// Household is excluded from the member-type options, so it never occurs here.
const mapMemberTypeToHouseholdRole = (
  memberTypeName: string | null | undefined
): AddUserHouseholdMemberPayload['household_role'] => {
  const lower = (memberTypeName ?? '').toLowerCase();
  if (lower.includes('co-head') || lower.includes('co_head')) {
    return 'co_hoh';
  }
  if (lower.includes('spouse')) {
    return 'spouse';
  }
  if (lower.includes('helper')) {
    return 'helper';
  }
  return 'member';
};

const AddHouseholdMemberScreen: FC<AddHouseholdMemberScreenProps> = ({
  household,
  certificationId,
  onSubmit,
  onCancel,
  sx = {},
}) => {
  const certId = certificationId ?? 0;
  const {
    householdMembersNotOnCert,
    isLoading: hookLoading,
    refetch,
  } = useCertificationMembersV2(certId);

  const [memberTypes, setMemberTypes] = useState<MemberType[]>([]);
  const [pickedMember, setPickedMember] = useState<UnlinkedHHM | null>(null);
  const [memberType, setMemberType] = useState<MemberType | null>(null);
  const [sendCredentials, setSendCredentials] = useState<'Yes' | 'No' | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<AlertMessage | null>(null);

  // Phase control: pre-select existing members, or fill in a new-person form.
  const [addPhase, setAddPhase] = useState<AddPhase>('select');
  const [isNewPerson, setIsNewPerson] = useState(false);
  // Tracks whether the user has already gone through a step-2 recovery so the
  // empty-list auto-redirect effect cannot override the recovery state.
  const hasRecoveredRef = useRef(false);

  // New-person form values (only used when isNewPerson is true).
  const [newFirstName, setNewFirstName] = useState('');
  const [newMiddleName, setNewMiddleName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newMemberType, setNewMemberType] = useState<MemberType | null>(null);
  const [newSendCredentials, setNewSendCredentials] = useState<
    'Yes' | 'No' | null
  >(null);

  // Reset send-credentials when the user clears all contact fields.
  useEffect(() => {
    if (!newEmail.trim() && !newPhone.trim()) {
      setNewSendCredentials(null);
    }
  }, [newEmail, newPhone]);

  // Fetch member types (excluding HoH; backend rejects it for this endpoint)
  useEffect(() => {
    const fetchTypes = async (): Promise<void> => {
      try {
        const res = await axios.get('household/household_member_type/');
        if (res.status === 200) {
          const filtered = (res.data as MemberType[]).filter(
            (mt) => mt.name.toLowerCase() !== 'head of household'
          );
          setMemberTypes(filtered);
        }
      } catch {
        setAlertMessage({
          type: 'error',
          message: 'Error loading form data. Please try again.',
        });
      }
    };
    void fetchTypes();
  }, []);

  // When there are no existing members to add, go straight to the new-person
  // Auto-redirect to new-person form when there are no existing members to pick.
  // Guarded by hasRecoveredRef so a step-2 failure recovery cannot be overridden
  // by a subsequent refetch that temporarily returns an empty list.
  useEffect(() => {
    if (
      !hookLoading &&
      householdMembersNotOnCert.length === 0 &&
      !hasRecoveredRef.current
    ) {
      setAddPhase('form');
      setIsNewPerson(true);
    }
  }, [hookLoading, householdMembersNotOnCert.length]);

  const hasContactInfo = useMemo(
    () =>
      Boolean(pickedMember?.primary_email) ||
      Boolean(pickedMember?.primary_phone),
    [pickedMember]
  );

  // If the picked UHM has no contact info but "Yes" was selected, clear it so
  // the form stays valid.
  useEffect(() => {
    if (!hasContactInfo && sendCredentials === 'Yes') {
      setSendCredentials(null);
    }
  }, [hasContactInfo, sendCredentials]);

  const isFormValid =
    pickedMember !== null && memberType !== null && sendCredentials !== null;

  const newEmailConflict = useMemo(
    () =>
      findMemberContactConflict(
        household.cert_members ?? [],
        newEmail,
        null,
        null
      ),
    [household.cert_members, newEmail]
  );

  const newPhoneConflict = useMemo(
    () =>
      findMemberContactConflict(
        household.cert_members ?? [],
        null,
        newPhone,
        null
      ),
    [household.cert_members, newPhone]
  );

  const isNewPersonFormValid =
    newFirstName.trim() !== '' &&
    newLastName.trim() !== '' &&
    newMemberType !== null &&
    newEmailConflict === null &&
    newPhoneConflict === null &&
    // If any contact info is provided, send-credentials must be answered.
    ((!newEmail.trim() && !newPhone.trim()) || newSendCredentials !== null);

  // Early-error rendering when context is missing
  const missingContext = !certificationId;

  const handleSubmit = async (): Promise<void> => {
    if (!certificationId) {
      return;
    }
    if (!pickedMember || !memberType || !sendCredentials) {
      return;
    }
    setLoading(true);
    setAlertMessage(null);
    try {
      await axios.post(`certification/${certificationId}/household/members/`, {
        user_household_member_id: pickedMember.user_household_member_id,
        member_type_id: memberType.id,
        send_credentials: sendCredentials === 'Yes',
      });
      refetch();
      onSubmit();
    } catch {
      setAlertMessage({
        type: 'error',
        message: 'Error adding member. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewPersonSubmit = async (): Promise<void> => {
    if (!certificationId || !newMemberType) {
      return;
    }
    if (!newFirstName.trim() || !newLastName.trim()) {
      return;
    }

    setLoading(true);
    setAlertMessage(null);
    let uhmCreated = false;

    try {
      // Step 1: Create User + UserHouseholdMember in the user's household.
      const payload: AddUserHouseholdMemberPayload = {
        user_household_id: household.user_household_id,
        first_name: newFirstName.trim(),
        last_name: newLastName.trim(),
        household_role: mapMemberTypeToHouseholdRole(newMemberType.name),
      };
      if (newMiddleName.trim()) {
        payload.middle_name = newMiddleName.trim();
      }
      if (newEmail.trim()) {
        payload.email = newEmail.trim().toLowerCase();
      }
      if (newPhone.trim()) {
        payload.phone_number = newPhone.trim();
      }

      const memberRes = await axios.post(
        'v2/renter-hub/me/my-household/members/',
        payload
      );
      const newUhm = memberRes.data as { user_household_member_id: number };
      uhmCreated = true;

      // Step 2: Link the new UserHouseholdMember to the certification.
      await axios.post(`certification/${certificationId}/household/members/`, {
        user_household_member_id: newUhm.user_household_member_id,
        member_type_id: newMemberType.id,
        send_credentials: newSendCredentials === 'Yes',
      });

      refetch();
      onSubmit();
    } catch (err: unknown) {
      if (uhmCreated) {
        // Step 1 succeeded but step 2 failed — the member exists in the
        // household but is not yet linked to the certification. Refetch so
        // they appear in the pre-select list, then guide the user to pick them.
        // Set hasRecoveredRef so the empty-list effect cannot override this state.
        hasRecoveredRef.current = true;
        refetch();
        setIsNewPerson(false);
        setAddPhase('select');
        setAlertMessage({
          type: 'warning',
          message:
            'The member was added to your household but could not be linked to this application or re-certification. Please select them from the list below to complete the process.',
        });
      } else {
        setAlertMessage({
          type: 'error',
          message: messageForHouseholdMemberError(
            err as { detail?: string; error_code?: string; status?: number },
            'Error adding member. Please try again.'
          ),
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const renderSelectPhase = (): JSX.Element => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select an existing household member to add to this certification, or add
        someone new.
      </Typography>

      <Stack gap={1.5} sx={{ flex: '1 1 auto', overflow: 'auto', pr: 1 }}>
        {householdMembersNotOnCert.map((uhm) => {
          const contactLine = uhm.primary_email || uhm.primary_phone || null;
          return (
            <Paper
              key={uhm.user_household_member_id}
              variant="outlined"
              sx={{
                p: 1.5,
                borderRadius: 2,
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
              }}
              onClick={(): void => {
                setPickedMember(uhm);
                setIsNewPerson(false);
                setAddPhase('form');
              }}
            >
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Typography fontWeight={700} color="text.secondary">
                    {getInitials(uhm.first_name, uhm.last_name) || '?'}
                  </Typography>
                </Box>
                <Stack flex={1} sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {getUhmLabel(uhm) || '—'}
                  </Typography>
                  {contactLine && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {contactLine}
                    </Typography>
                  )}
                </Stack>
                <ChevronRightIcon
                  sx={{ color: 'text.disabled', flexShrink: 0 }}
                />
              </Stack>
            </Paper>
          );
        })}

        {/* Add New Member — inline card at the bottom of the list */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: 2,
            cursor: 'pointer',
            borderStyle: 'dashed',
            '&:hover': { borderColor: 'primary.main', boxShadow: 1 },
          }}
          onClick={(): void => {
            setPickedMember(null);
            setIsNewPerson(true);
            setAddPhase('form');
          }}
        >
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: 'primary.lighter',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <PersonAddIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            </Box>
            <Typography variant="body2" fontWeight={600} color="primary.main">
              Add New Member
            </Typography>
            <ChevronRightIcon
              sx={{ color: 'primary.main', flexShrink: 0, ml: 'auto' }}
            />
          </Stack>
        </Paper>
      </Stack>

      <Button
        variant="text"
        size="small"
        onClick={onCancel}
        sx={{ mt: 1, alignSelf: 'center' }}
      >
        Cancel
      </Button>
    </>
  );

  const renderFormPhase = (): JSX.Element => (
    <>
      {householdMembersNotOnCert.length > 0 && (
        <Button
          variant="text"
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={(): void => {
            setAddPhase('select');
            setPickedMember(null);
            setIsNewPerson(false);
          }}
          sx={{ alignSelf: 'flex-start', mb: 1 }}
        >
          <span>Back</span>
        </Button>
      )}

      <Stack gap={2} sx={{ pr: 1, flex: '1 1 100%', overflow: 'auto' }}>
        {isNewPerson ? (
          <>
            {/* Hero placeholder */}
            {/* Personal Information */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                Personal Information
              </Typography>
              <Stack gap={2}>
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="First Name"
                  value={newFirstName}
                  onChange={(e) => setNewFirstName(e.target.value)}
                />
                <TextField
                  size="small"
                  fullWidth
                  label="Middle Name"
                  value={newMiddleName}
                  onChange={(e) => setNewMiddleName(e.target.value)}
                />
                <TextField
                  size="small"
                  fullWidth
                  required
                  label="Last Name"
                  value={newLastName}
                  onChange={(e) => setNewLastName(e.target.value)}
                />
                <Autocomplete
                  fullWidth
                  options={memberTypes}
                  getOptionLabel={(option): string => option.display_name}
                  value={newMemberType}
                  onChange={(_event, newValue): void =>
                    setNewMemberType(newValue)
                  }
                  isOptionEqualToValue={(opt, val): boolean =>
                    opt.id === val.id
                  }
                  renderInput={(params): JSX.Element => (
                    <TextField
                      {...params}
                      size="small"
                      label="Relationship with Head of Household"
                      required
                    />
                  )}
                />
              </Stack>
            </Paper>

            {/* Contact Information — hidden for minor/dependent member types */}
            {!isMinorMemberType(
              newMemberType?.display_name ?? newMemberType?.name
            ) && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                  Contact Information
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Contact information is used to send this member important
                  notifications and forms. This is optional.
                </Typography>
                <Stack gap={2}>
                  <TextField
                    size="small"
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="email@example.com"
                    error={Boolean(newEmailConflict)}
                    helperText={
                      newEmailConflict
                        ? 'This email is already used by another household member'
                        : undefined
                    }
                  />
                  <TextField
                    size="small"
                    fullWidth
                    label="Phone Number"
                    type="tel"
                    value={newPhone}
                    onChange={(e) => setNewPhone(e.target.value)}
                    placeholder="(555) 000-0000"
                    error={Boolean(newPhoneConflict)}
                    helperText={
                      newPhoneConflict
                        ? 'This phone number is already used by another household member'
                        : undefined
                    }
                  />
                  {(newEmail.trim() || newPhone.trim()) && (
                    <Autocomplete
                      size="small"
                      options={['Yes', 'No'] as const}
                      value={newSendCredentials}
                      onChange={(_e, v): void => setNewSendCredentials(v)}
                      getOptionLabel={(o): string => o}
                      renderInput={(params): JSX.Element => (
                        <TextField
                          {...params}
                          label="Send Invitation"
                          required
                        />
                      )}
                    />
                  )}
                </Stack>
              </Paper>
            )}
          </>
        ) : (
          <>
            {/* Picked member hero (read-only) */}
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <Typography fontWeight={700} color="grey.700">
                  {getInitials(
                    pickedMember?.first_name,
                    pickedMember?.last_name
                  ) || '?'}
                </Typography>
              </Box>
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant="body1" fontWeight={600}>
                  {pickedMember ? getUhmLabel(pickedMember) : ''}
                </Typography>
                {(pickedMember?.primary_email ||
                  pickedMember?.primary_phone) && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {pickedMember?.primary_email || pickedMember?.primary_phone}
                  </Typography>
                )}
              </Stack>
            </Paper>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                Member Information
              </Typography>
              <Stack gap={2}>
                <Autocomplete
                  fullWidth
                  size="small"
                  options={memberTypes}
                  getOptionLabel={(option): string => option.display_name}
                  value={memberType}
                  onChange={(_event, newValue): void => setMemberType(newValue)}
                  isOptionEqualToValue={(opt, val): boolean =>
                    opt.id === val.id
                  }
                  renderInput={(params): JSX.Element => (
                    <TextField
                      {...params}
                      label="Relationship with Head of Household"
                      required
                    />
                  )}
                />

                <Autocomplete
                  size="small"
                  options={['Yes', 'No'] as const}
                  value={sendCredentials}
                  onChange={(_event, newValue): void =>
                    setSendCredentials(newValue)
                  }
                  getOptionDisabled={(option): boolean =>
                    option === 'Yes' && !hasContactInfo
                  }
                  getOptionLabel={(option): string =>
                    option === 'Yes' && !hasContactInfo
                      ? 'Yes - Please add contact information'
                      : option
                  }
                  renderInput={(params): JSX.Element => (
                    <TextField
                      {...params}
                      label="Send Invitation"
                      helperText="Send login credentials to the member via email or SMS"
                      required
                    />
                  )}
                />

                {sendCredentials === 'Yes' && !hasContactInfo && (
                  <Alert severity="error">
                    This member has no email or phone — please add contact info
                    in Account Hub before sending an invitation.
                  </Alert>
                )}
              </Stack>
            </Paper>
          </>
        )}
      </Stack>

      <Stack sx={{ mt: 2 }}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={isNewPerson ? handleNewPersonSubmit : handleSubmit}
          disabled={
            loading ||
            (isNewPerson
              ? !isNewPersonFormValid
              : !isFormValid || missingContext)
          }
          size="large"
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          <span>{loading ? 'Adding…' : 'Add Member'}</span>
        </Button>
      </Stack>
    </>
  );

  return (
    <Stack sx={{ height: '100%', ...fadeStyle, ...sx }}>
      {alertMessage && (
        <Alert
          severity={alertMessage.type}
          sx={{ mb: 2, mt: 1 }}
          onClose={(): void => setAlertMessage(null)}
        >
          {alertMessage.message}
        </Alert>
      )}

      {missingContext && (
        <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
          Missing certification context — cannot add members.
        </Alert>
      )}

      {!missingContext && hookLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!missingContext &&
        !hookLoading &&
        (addPhase === 'select' ? renderSelectPhase() : renderFormPhase())}
    </Stack>
  );
};

export default AddHouseholdMemberScreen;
